import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Send, MoreVertical, Flag, Ban, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { format, isToday, isYesterday } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useTranslation } from 'react-i18next';
import { localizeCity } from '../utils/cityLocale';

import API_URL from '../config/api';
import SEOHead from '../components/SEOHead';

export default function Chat() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').substring(0, 2);
  
  const [messages, setMessages] = useState([]);
  const [matchData, setMatchData] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/${matchId}`, { withCredentials: true });
      setMessages(response.data.messages);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('You cannot access this conversation');
        navigate('/matches');
      }
    }
  }, [matchId, navigate]);

  const fetchMatchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/matches`, { withCredentials: true });
      const match = response.data.matches.find(m => m.match_id === matchId);
      if (match) {
        setMatchData(match);
      } else {
        toast.error('Match not found');
        navigate('/matches');
      }
    } catch (err) {
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [matchId, navigate]);

  useEffect(() => {
    fetchMatchData();
    fetchMessages();
    
    // Poll for new messages
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchMatchData, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${API_URL}/api/messages`, {
        match_id: matchId,
        content: newMessage.trim()
      }, { withCredentials: true });
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      inputRef.current?.focus();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/report`, {
        reported_user_id: matchData?.user?.user_id,
        reason: reportReason
      }, { withCredentials: true });
      
      toast.success('Report submitted');
      setShowReportDialog(false);
      setReportReason('');
    } catch (err) {
      toast.error('Failed to submit report');
    }
  };

  const handleBlock = async () => {
    try {
      await axios.post(`${API_URL}/api/block`, {
        blocked_user_id: matchData?.user?.user_id
      }, { withCredentials: true });
      
      toast.success('User blocked');
      navigate('/matches');
    } catch (err) {
      toast.error('Failed to block user');
    }
  };

  // Get photo URL
  const getPhotoUrl = (profile) => {
    if (profile?.primary_photo?.photo_id) {
      return `${API_URL}/api/photos/${profile.primary_photo.photo_id}?auth=placeholder`;
    }
    if (profile?.google_picture) {
      return profile.google_picture;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(profile?.name || 'U')}&background=6E1A24&color=fff&size=100`;
  };

  // Format message date
  const formatMessageDate = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return 'Yesterday ' + format(date, 'HH:mm');
    }
    return format(date, 'MMM d, HH:mm');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#6E1A24] animate-spin" />
      </div>
    );
  }

  const otherUser = matchData?.user;

  return (
    <div className="h-screen bg-[#FAF9F6] flex flex-col">
      <SEOHead title="Messages" noindex path="/chat" />
      {/* Header */}
      <header className="flex-shrink-0 bg-white border-b border-[#E5E3DB] px-4 py-3 flex items-center gap-3 safe-area-top">
        <button 
          onClick={() => navigate('/matches')}
          className="p-2 -ml-2 hover:bg-[#F5F2EB] rounded-full"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        
        <Link to={`/profile/${otherUser?.user_id}`} className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-full overflow-hidden">
            <img 
              src={getPhotoUrl(otherUser)}
              alt={otherUser?.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h2 className="font-medium text-[#2D2D2A]">
              {otherUser?.nickname || otherUser?.name}
            </h2>
            {otherUser?.city && (
              <p className="text-xs text-[#6C6C67]">{localizeCity(otherUser.city, lang)}</p>
            )}
          </div>
        </Link>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 hover:bg-[#F5F2EB] rounded-full" data-testid="chat-menu">
              <MoreVertical className="w-5 h-5 text-[#6C6C67]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setShowReportDialog(true)} data-testid="report-btn">
              <Flag className="w-4 h-4 mr-2" />
              Report
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setShowBlockDialog(true)}
              className="text-red-600"
              data-testid="block-btn"
            >
              <Ban className="w-4 h-4 mr-2" />
              Block
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-20 h-20 rounded-full overflow-hidden mb-4">
              <img 
                src={getPhotoUrl(otherUser)}
                alt={otherUser?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <h3 className="heading-4 mb-2">
              You matched with {otherUser?.nickname || otherUser?.name}!
            </h3>
            <p className="body-small max-w-xs">
              Send a message to start the conversation
            </p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const isMe = msg.sender_id === user?.user_id;
            return (
              <div 
                key={msg.message_id || i}
                className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[75%] ${isMe ? 'chat-bubble-me' : 'chat-bubble-match'}`}>
                  <p className="break-words">{msg.content}</p>
                  <p className={`text-xs mt-1 ${isMe ? 'text-white/60' : 'text-[#6C6C67]'}`}>
                    {formatMessageDate(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form 
        onSubmit={handleSend}
        className="flex-shrink-0 bg-white border-t border-[#E5E3DB] px-4 py-3 flex gap-3 safe-area-bottom"
      >
        <input
          ref={inputRef}
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 bg-[#F5F2EB] rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#6E1A24]/20"
          data-testid="message-input"
        />
        <Button
          type="submit"
          disabled={!newMessage.trim() || sending}
          className="w-10 h-10 rounded-full bg-[#6E1A24] hover:bg-[#8A2331] p-0 flex items-center justify-center"
          data-testid="send-btn"
        >
          {sending ? (
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          ) : (
            <Send className="w-5 h-5 text-white" />
          )}
        </Button>
      </form>

      {/* Report Dialog */}
      <AlertDialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Report User</AlertDialogTitle>
            <AlertDialogDescription>
              Please describe why you're reporting this user. Our team will review your report.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <textarea
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder="Describe the issue..."
            className="w-full p-3 border border-[#E5E3DB] rounded-xl focus:outline-none focus:border-[#6E1A24] resize-none h-24"
            data-testid="report-reason"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReport} data-testid="submit-report">
              Submit Report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Block Dialog */}
      <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Block User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to block {otherUser?.nickname || otherUser?.name}? 
              They won't be able to contact you anymore.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBlock}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-block"
            >
              Block User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
